import type { ComponentType, ReactNode } from 'react';
import { AlertTriangle, Inbox, Loader2, LockKeyhole } from 'lucide-react';
import { cn } from '../lib/utils';

type StateTone = 'neutral' | 'primary' | 'warning' | 'danger';

const toneClass: Record<StateTone, string> = {
  neutral: 'bg-slate-50 text-slate-500 ring-slate-200',
  primary: 'bg-primary-50 text-primary-600 ring-primary-100',
  warning: 'bg-warning-50 text-warning-600 ring-warning-100',
  danger: 'bg-error-50 text-error-600 ring-error-100',
};

export type LoadingSpinnerSize = 'sm' | 'md' | 'lg';

const spinnerSizeClass: Record<LoadingSpinnerSize, string> = {
  sm: 'h-4 w-4',
  md: 'h-8 w-8',
  lg: 'h-12 w-12',
};

interface LoadingStateProps {
  className?: string;
  label?: string;
}

export interface LoadingSpinnerProps {
  className?: string;
  size?: LoadingSpinnerSize;
  /** Accessible name for screen readers. */
  label?: string;
}

export interface StateViewProps {
  icon?: ComponentType<{ className?: string }>;
  title: string;
  description?: string;
  children?: ReactNode;
  className?: string;
  tone?: StateTone;
}

/** Compact spinner for inline and centered route/auth gates. */
export function LoadingSpinner({
  className,
  size = 'md',
  label = '加载中',
}: LoadingSpinnerProps) {
  return (
    <Loader2
      className={cn('animate-spin text-primary-600', spinnerSizeClass[size], className)}
      aria-label={label}
      role="status"
    />
  );
}

/** Labeled block loading state (section / empty-panel height). */
export function LoadingState({ className, label = '加载中' }: LoadingStateProps) {
  return (
    <div
      className={cn(
        'flex min-h-[320px] flex-col items-center justify-center gap-3 text-slate-500',
        className
      )}
    >
      <LoadingSpinner label={label} />
      <p className="text-sm">{label}</p>
    </div>
  );
}

export function StateView({
  icon: Icon = Inbox,
  title,
  description,
  children,
  className,
  tone = 'neutral',
}: StateViewProps) {
  return (
    <section
      className={cn(
        'mx-auto flex max-w-xl flex-col items-center px-4 py-12 text-center',
        className
      )}
    >
      <div
        className={cn(
          'flex h-12 w-12 items-center justify-center rounded-lg ring-1',
          toneClass[tone]
        )}
      >
        <Icon className="h-6 w-6" />
      </div>
      <h2 className="mt-4 text-lg font-semibold text-slate-950">{title}</h2>
      {description && <p className="mt-2 text-sm leading-6 text-slate-500">{description}</p>}
      {children && (
        <div className="mt-5 flex flex-wrap items-center justify-center gap-2">{children}</div>
      )}
    </section>
  );
}

export function EmptyState(props: Omit<StateViewProps, 'icon' | 'tone'>) {
  return <StateView icon={Inbox} tone="neutral" {...props} />;
}

export function ErrorState({
  message,
  ...props
}: Omit<StateViewProps, 'description' | 'icon' | 'tone'> & { message: string }) {
  return <StateView icon={AlertTriangle} tone="danger" description={message} {...props} />;
}

export function LockedState(props: Omit<StateViewProps, 'icon' | 'tone'>) {
  return <StateView icon={LockKeyhole} tone="warning" {...props} />;
}
